import * as React from 'react';
import { isEmptyChildren, isFunction } from './react-utils';
import { gql, useApolloClient } from '@apollo/client';
import { Modal, Typography } from 'antd';
import {
  authStateChangedAction,
  signOutAction,
} from '../redux-store/auth-store';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { userProfileAttributes } from '../pages/users/constants';
import client from '../apollo-client';

const { Text } = Typography;

const signIntoAdminMutation = gql`
  mutation SignIntoAdmin($username: String!, $password: String!) {
    signIntoAdmin(username: $username, password: $password) {
      token
      challengeId
      email
      mustResetPassword
      emailSent
    }
  }
`;

const verifyTwoFactorForAdminMutation = gql`
  mutation VerifyTwoFactorForAdmin($challengeId: ID!, $code: String!) {
    verifyTwoFactorForAdmin(challengeId: $challengeId, code: $code) {
      token
      email
      mustResetPassword
      emailSent
    }
  }
`;

const signIntoAdminWithMagicLinkMutation = gql`
  mutation SignIntoAdminWithMagicLink($code: String!) {
    signIntoAdminWithMagicLink(code: $code) {
      token
    }
  }
`;

const signIntoAdminWithGoogleMutation = gql`
  mutation signIntoAdminWithGoogle(
    $authResponseCode: String!
    $origin: String!
  ) {
    signIntoAdminWithGoogle(
      authResponseCode: $authResponseCode
      origin: $origin
    ) {
      token
    }
  }
`;

const myProfileQuery = gql`
  query myProfileForAdmin {
    myProfileForAdmin ${userProfileAttributes}
  }
`;

const initialAuthState = {
  isLoading: true,
  isSignout: false,
  user: null,
};

export const AuthContext = React.createContext({
  signIn: async () => '',
  signInWithMagicLink: async () => '',
  signOut: () => null,
  signUp: async () => '',
  state: initialAuthState,
});

function processErrorMessage(err) {
  console.log(err.message);
  if (err.message.includes('has not been assigned a role')) {
    Modal.info({
      title: 'Please contact your administrator',
      content: (
        <Text>{`The system recognizes your account, but your profile hasn't yet been assigned a role.  Please contact your administrator at admin@aims.org.`}</Text>
      ),
      onOk() {},
    });
    return undefined;
  }
  if (err.message.includes('does not exist')) {
    return 'Username and/or password incorrect';
  }
  if (err.message.includes('Bad or expired magic link')) {
    return 'Bad or expired link.  Please sign in.';
  }
  return err.message;
}

async function finishSignIn(token, dispatch) {
  localStorage.setItem('authToken', token);
  const response = await client.query({
    query: myProfileQuery,
    fetchPolicy: 'network-only',
  });
  if (!response || !response.data || !response.data.myProfileForAdmin) {
    throw new Error('Error trying to sign in');
  }
  dispatch(authStateChangedAction(response.data.myProfileForAdmin));
}

export const AuthContextProvider = (props) => {
  const { component, children } = props;

  const dispatch = useDispatch();
  const authState = useSelector((store) => store.authState, shallowEqual);
  const currentUser = useSelector((store) => store.profile, shallowEqual);
  const state = React.useMemo(
    () => ({
      ...authState,
      user: currentUser,
    }),
    [authState, currentUser],
  );

  React.useEffect(() => {
    const doAsyncStuff = async () => {
      const token = localStorage.getItem('authToken');
      try {
        if (!token) {
          throw new Error('Not signed in');
        }
        let response;
        response = await client.query({
          query: myProfileQuery,
          fetchPolicy: 'network-only',
        });
        if (!response || !response.data || !response.data.myProfileForAdmin) {
          throw new Error('Error trying to sign in');
        }
        dispatch(authStateChangedAction(response.data.myProfileForAdmin));
      } catch (err) {
        console.error(err);
        localStorage.removeItem('authToken');
        dispatch(authStateChangedAction(null));
        client.close();
        // notification.open({
        //   message: 'Error',
        //   description: err.message,
        // });
      }
    };
    doAsyncStuff();
  }, [dispatch]);

  const authContext = React.useMemo(
    () => ({
      signIn: async (values) => {
        const { username, password } = values;
        try {
          let response = await client.mutate({
            mutation: signIntoAdminMutation,
            variables: {
              username: username,
              password: password,
            },
            fetchPolicy: 'no-cache',
          });
          const result = response?.data?.signIntoAdmin;
          if (result?.token) {
            await finishSignIn(result.token, dispatch);
            client.close();
            return '';
          }
          if (result?.challengeId || result?.mustResetPassword) {
            return result;
          }
          throw new Error('Error trying to sign in');
        } catch (err) {
          localStorage.removeItem('authToken');
          client.close();
          return processErrorMessage(err);
        }
      },
      verifyTwoFactor: async (challengeId, code) => {
        try {
          let response = await client.mutate({
            mutation: verifyTwoFactorForAdminMutation,
            variables: {
              challengeId,
              code,
            },
            fetchPolicy: 'no-cache',
          });
          const result = response?.data?.verifyTwoFactorForAdmin;
          if (result?.mustResetPassword) {
            client.close();
            return result;
          }
          if (!result?.token) {
            throw new Error('Error trying to sign in');
          }
          await finishSignIn(result.token, dispatch);
          client.close();
        } catch (err) {
          localStorage.removeItem('authToken');
          client.close();
          return processErrorMessage(err);
        }
        return '';
      },
      signInWithMagicLink: async (code) => {
        try {
          let response = await client.mutate({
            mutation: signIntoAdminWithMagicLinkMutation,
            variables: {
              code,
            },
            fetchPolicy: 'no-cache',
          });
          const token = response?.data?.signIntoAdminWithMagicLink?.token;
          if (!token) {
            throw new Error('Error trying to sign in');
          }
          await finishSignIn(token, dispatch);
          client.close();
        } catch (err) {
          localStorage.removeItem('authToken');
          client.close();
          return processErrorMessage(err);
        }
        return '';
      },
      signIntoAdminWithGoogle: async (authResponseCode) => {
        try {
          let response = await client.mutate({
            mutation: signIntoAdminWithGoogleMutation,
            variables: {
              authResponseCode,
              origin: window.location.origin,
            },
            fetchPolicy: 'no-cache',
          });
          const token = response?.data?.signIntoAdminWithGoogle?.token;
          if (!token) {
            throw new Error('Error trying to sign in');
          }
          await finishSignIn(token, dispatch);
          client.close();
        } catch (err) {
          console.error(err);
          localStorage.removeItem('authToken');
          client.close();
          return processErrorMessage(err);
        }
        return '';
      },
      signOut: async () => {
        localStorage.removeItem('authToken');
        dispatch(signOutAction());
      },
      state,
    }),
    [state, dispatch],
  );

  return (
    <AuthContext.Provider value={authContext}>
      {component
        ? React.createElement(component, authContext)
        : children // children come last, always called
        ? isFunction(children)
          ? children(authContext)
          : !isEmptyChildren(children)
          ? React.Children.only(children)
          : null
        : null}
    </AuthContext.Provider>
  );
};
